import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import Cabecalho from '@/components/ui/Cabecalho';
import EstadoVazio from '@/components/ui/EstadoVazio';
import Dica from '@/components/feature/Dica';
import CartaoRifa from './components/CartaoRifa';
import type { RifaMinha } from './types';

export default function MinhasRifasPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [rifas, setRifas] = useState<RifaMinha[]>([]);
  const [capas, setCapas] = useState<Record<number, string>>({});
  const [encerradosAberto, setEncerradosAberto] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(`/entrar?voltar=${encodeURIComponent('/minhas-rifas')}`, { replace: true });
      return;
    }
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        await garantirUsuario();

        const { data, error } = await supabase.rpc('minhas_rifas');
        if (error) throw error;

        const lista = (data as RifaMinha[]) || [];
        if (ativo) setRifas(lista);

        // Capas dos sorteios (só os que aparecem na vitrine pública).
        // Usa `capa_da_rifa` → o campo `imagem` já resolve capa própria › foto do 1º prêmio.
        const ids = lista.filter((r) => r.status === 'publicada').map((r) => r.rifa_id);
        if (ids.length > 0) {
          const resultados = await Promise.all(
            ids.map((id) => supabase.rpc('capa_da_rifa', { p_rifa: id })),
          );
          const mapa: Record<number, string> = {};
          ids.forEach((id, idx) => {
            const c = resultados[idx]?.data as { imagem?: string | null } | null;
            if (c?.imagem) mapa[id] = c.imagem;
          });
          if (ativo) setCapas(mapa);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar suas ações entre amigos. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, reload, pronto, user]);

  const ativos = useMemo(() => rifas.filter((r) => r.status === 'publicada'), [rifas]);
  const encerrados = useMemo(() => rifas.filter((r) => r.status !== 'publicada'), [rifas]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <Cabecalho destaque larguraMaxima="max-w-[1040px]" titulo="Ação entre amigos" subtitulo="Os talões que você tem para vender." aoLadoDoTitulo={<Dica id="minhas-rifas" />} />
        <div className="mx-auto w-full max-w-[1040px] px-4 py-8 md:px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[repeat(auto-fill,minmax(440px,1fr))]">
            <div className="h-80 animate-pulse rounded-[22px] bg-secondary-100" />
            <div className="h-80 animate-pulse rounded-[22px] bg-secondary-100" />
          </div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="min-h-screen bg-background-100">
        <Cabecalho destaque larguraMaxima="max-w-[1040px]" titulo="Ação entre amigos" subtitulo="Os talões que você tem para vender." aoLadoDoTitulo={<Dica id="minhas-rifas" />} />
        <div className="mx-auto w-full max-w-[420px] px-4 py-8">
          <div className="overflow-hidden rounded-3xl border border-secondary-100 bg-white">
            <div className="p-6 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-error-warning-line text-2xl" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-foreground-950">Não foi possível carregar</h1>
              <p className="mt-2 text-sm text-foreground-600">{erro}</p>
              <button
                type="button"
                onClick={() => setReload((r) => r + 1)}
                className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Tentar de novo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <Cabecalho destaque larguraMaxima="max-w-[1040px]" titulo="Ação entre amigos" subtitulo="Os talões que você tem para vender." aoLadoDoTitulo={<Dica id="minhas-rifas" />} />

      <div className="mx-auto w-full max-w-[1040px] px-4 py-8 md:px-6">
        {rifas.length === 0 ? (
          <EstadoVazio
            icone="ri-ticket-2-line"
            titulo="Você ainda não tem talão"
            descricao="Quando a organização te entregar um, ele aparece aqui."
          />
        ) : (
          <>
            {/* Sorteios ativos */}
            {ativos.length === 1 ? (
              <div className="max-w-[640px]">
                <CartaoRifa rifa={ativos[0]} imagem={capas[ativos[0].rifa_id] ?? null} />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[repeat(auto-fill,minmax(440px,1fr))]">
                {ativos.map((rifa) => (
                  <CartaoRifa key={rifa.rifa_id} rifa={rifa} imagem={capas[rifa.rifa_id] ?? null} />
                ))}
              </div>
            )}

            {/* Encerrados (colapsado) */}
            {encerrados.length > 0 && (
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => setEncerradosAberto((v) => !v)}
                  className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-background-50"
                  aria-expanded={encerradosAberto}
                >
                  <span className="text-sm font-semibold text-secondary-500">Encerrados</span>
                  <span className="flex h-6 w-6 items-center justify-center text-secondary-400">
                    <i className={encerradosAberto ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line text-lg'} />
                  </span>
                </button>

                {encerradosAberto && (
                  <ul className="mt-1 divide-y divide-secondary-100">
                    {encerrados.map((rifa) => (
                      <li key={rifa.rifa_id} className="flex items-center justify-between gap-3 py-3">
                        <span className="text-sm font-medium text-foreground-800">{rifa.titulo}</span>
                        <span className="whitespace-nowrap text-xs text-secondary-500">
                          {rifa.status === 'cancelada'
                            ? 'cancelada'
                            : rifa.status === 'sorteada' || rifa.status === 'encerrada'
                              ? 'apurada'
                              : rifa.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}